import React, { useContext, useMemo, useState } from "react";
import {
  Typography,
  Chip,
  TextField,
  Button,
  Box,
  Stack,
} from "@mui/material";
import DataTable from "../../dataTable/DataTable";
import ListSearchField from "../../listSearchField/ListSearchField";
import { useEffectOnce } from "../../../utils/effects";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "../../../context/AuthContext";
import { formatUtcToLocal, getLocalTzLabel } from "../../../services/dateUtils";
import {
  GridContainer,
  KycStatusListRootArea,
  GridItem,
  FilterChipsRow,
} from "./KycStatusList.styles";
import {
  fetchKycVerifications,
} from "./KycStatusList.thunk";
import { setStatusFilter } from "./KycStatusList.slice";
import {
  formatKycStatusChip,
  formatClientDisplayName,
  formatPoiStatusChip,
  isKycPlatformAdmin,
  resolveKycMerchantId,
} from "./kycStatusUtils";
import KycVerificationDetailModal from "./KycVerificationDetailModal";

const tzLabel = getLocalTzLabel();
const withTz = (label) => (tzLabel ? `${label} (${tzLabel})` : label);

const truncateText = (value, maxLength = 48) => {
  if (!value) return "-";
  const text = String(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}…`;
};

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const KycStatusList = () => {
  const dispatch = useDispatch();
  const { token, userInfo, acquirerData } = useContext(AuthContext);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchMerchantId, setSearchMerchantId] = useState("");
  const [appliedSearch, setAppliedSearch] = useState({
    search: "",
    merchant_id: "",
  });

  const {
    verificationsList,
    status,
    page,
    pageSize,
    totalRecords,
    statusFilter,
  } = useSelector((state) => state.kycStatusList);

  const isAdmin = isKycPlatformAdmin(userInfo);
  const merchantId = resolveKycMerchantId(userInfo, acquirerData);

  const scopeLabel = useMemo(() => {
    if (isAdmin) {
      if (appliedSearch.merchant_id) {
        return `Merchant: ${appliedSearch.merchant_id}`;
      }
      return "Platform (all merchants)";
    }
    return merchantId ? `Merchant: ${merchantId}` : "Merchant";
  }, [isAdmin, appliedSearch.merchant_id, merchantId]);

  const buildListPayload = (overrides = {}) => {
    const payload = {
      token,
      page: overrides.page ?? page,
      page_size: overrides.page_size ?? pageSize,
      status_filter: overrides.status_filter ?? statusFilter,
      is_admin: isAdmin,
    };

    const search =
      "search" in overrides ? overrides.search : appliedSearch.search;
    if (search) {
      payload.search = search;
    }

    if (isAdmin) {
      const mid =
        "merchant_id" in overrides
          ? overrides.merchant_id
          : appliedSearch.merchant_id;
      if (mid) payload.merchant_id = mid;
    } else {
      payload.merchant_id = merchantId;
    }

    return payload;
  };

  const loadList = (overrides = {}) => {
    dispatch(fetchKycVerifications(buildListPayload(overrides)));
  };

  useEffectOnce(() => {
    loadList();
  });

  const handleStatusFilterClick = (filterValue) => {
    dispatch(setStatusFilter(filterValue));
    loadList({ status_filter: filterValue, page: 1 });
  };

  const handlePageChange = (event, newPage) => {
    loadList({ page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    loadList({ page: 1, page_size: newPageSize });
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleSearch = () => {
    const nextSearch = {
      search: searchQuery.trim(),
      merchant_id: isAdmin ? searchMerchantId.trim() : "",
    };
    setAppliedSearch(nextSearch);
    loadList({
      page: 1,
      search: nextSearch.search || undefined,
      merchant_id: nextSearch.merchant_id || undefined,
    });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchMerchantId("");
    const cleared = { search: "", merchant_id: "" };
    setAppliedSearch(cleared);
    loadList({
      page: 1,
      search: "",
      merchant_id: "",
      status_filter: statusFilter,
    });
  };

  const emptyStateText = useMemo(() => {
    if (statusFilter === "all") {
      return "No KYC verifications found";
    }
    const label =
      STATUS_FILTERS.find((filter) => filter.value === statusFilter)?.label ||
      statusFilter;
    return `No ${label.toLowerCase()} KYC verifications found`;
  }, [statusFilter]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    {
      id: "display_name",
      label: "Client",
      minWidth: 180,
      format: (value, row) => formatClientDisplayName(row),
    },
    { id: "email", label: "Email", minWidth: 180 },
    ...(isAdmin
      ? [{ id: "merchant_id", label: "Merchant ID", minWidth: 130 }]
      : []),
    {
      id: "person_name",
      label: "Contact",
      minWidth: 150,
      format: (value, row) => {
        const company = String(row?.company_name || "").trim();
        const firstName = row?.first_name || "";
        const lastName = row?.last_name || "";
        const fullName = `${firstName} ${lastName}`.trim();
        if (company && fullName) return fullName;
        return fullName || "-";
      },
    },
    { id: "provider_code", label: "Provider", minWidth: 90 },
    {
      id: "provider_account_key",
      label: "TRIA Account",
      minWidth: 120,
    },
    {
      id: "status",
      label: "KYC Status",
      minWidth: 110,
      format: (value) => {
        const chip = formatKycStatusChip(value);
        if (!chip) return "-";
        return (
          <Chip size="small" label={chip.label} sx={chip.sx} />
        );
      },
    },
    {
      id: "poi_status",
      label: "POI Status",
      minWidth: 110,
      format: (value) => {
        const chip = formatPoiStatusChip(value);
        if (!chip) return "-";
        return (
          <Chip size="small" label={chip.label} sx={chip.sx} />
        );
      },
    },
    {
      id: "declined_reason",
      label: "Last error",
      minWidth: 160,
      format: (value) => truncateText(value),
    },
    {
      id: "retries_left",
      label: "Retries left",
      minWidth: 90,
      format: (value) =>
        value === 0 || value ? String(value) : "-",
    },
    {
      id: "attempts",
      label: "Attempts",
      minWidth: 80,
      format: (value) =>
        Array.isArray(value) ? String(value.length) : "0",
    },
    {
      id: "updated_at",
      label: withTz("Updated At"),
      minWidth: 150,
      format: (value) => formatUtcToLocal(value) || "-",
    },
  ];

  return (
    <KycStatusListRootArea>
      <GridContainer>
        <GridItem xs={12}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mt: 2, mb: 1 }}
          >
            <Typography variant="h6">KYC Verifications</Typography>
            <Chip
              size="small"
              label={scopeLabel}
              color={isAdmin && !appliedSearch.merchant_id ? "info" : "default"}
              variant="outlined"
            />
          </Stack>
        </GridItem>

        <GridItem xs={12}>
          <FilterChipsRow>
            {STATUS_FILTERS.map((filter) => (
              <Chip
                key={filter.value}
                label={filter.label}
                clickable
                color={statusFilter === filter.value ? "primary" : "default"}
                variant={statusFilter === filter.value ? "filled" : "outlined"}
                onClick={() => handleStatusFilterClick(filter.value)}
              />
            ))}
          </FilterChipsRow>
        </GridItem>

        <GridItem xs={12}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <ListSearchField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={handleSearch}
              label="Search (Client ID or email)"
              placeholder={isAdmin ? "e.g. 1001 or payer@example.com" : "Client ID or email"}
              sx={{ minWidth: { xs: "100%", sm: 280 }, flexGrow: 1 }}
            />
            {isAdmin && (
              <TextField
                label="Merchant ID (optional)"
                size="small"
                value={searchMerchantId}
                onChange={(e) => setSearchMerchantId(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                sx={{ minWidth: { xs: "100%", sm: 180 } }}
              />
            )}
            <Button variant="text" size="small" onClick={handleClearSearch}>
              Clear
            </Button>
          </Box>
        </GridItem>

        <GridItem xs={12}>
          <DataTable
            loading={status === "loading"}
            columns={columns}
            rows={verificationsList}
            page={page - 1}
            rowsPerPage={pageSize}
            count={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onRowClick={handleRowClick}
            noDataText={emptyStateText}
          />
        </GridItem>
      </GridContainer>

      <KycVerificationDetailModal
        open={modalOpen}
        row={selectedRow}
        onClose={handleCloseModal}
      />
    </KycStatusListRootArea>
  );
};

export default KycStatusList;
